import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { toastError } from '@/lib/toast';
import { Loader2, Paperclip, StickyNote, Trash2, X } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
  useTicketNotes,
  useAddTicketNote,
  useDeleteTicketNote,
  useTeamMembers,
  useTicket,
  type NoteAttachment,
  type TeamMember,
  type TicketNote,
} from '@/lib/queries';
import { useAuth } from '@/lib/auth-guard';
import { useAccess } from '@/lib/use-access';
import { supabase } from '@/lib/supabase';
import { assigneeName } from '@/lib/format';
import { RelativeTime } from '@/components/RelativeTime';
import { cn } from '@/lib/utils';
import { Sparkles } from 'lucide-react';
import { UserAvatar } from '@/components/UserAvatar';
import { AttachmentTile } from '@/components/AttachmentTile';
import { EmojiPickerButton } from '@/components/EmojiPicker';
import { RailSection } from '@/components/RailSection';
import { EmptyState, NotesPanelSkeleton } from '@/components/Skeletons';
import {
  NOTE_ATTACHMENT_MAX_PER_NOTE,
  uploadNoteAttachments,
} from '@/lib/note-attachments';
import { formatBytes } from '@/lib/assist-attachments';

// Internal team-only commentary on a ticket. Different from email replies:
// - Never sent to the client
// - Visible to all active team_members
// - Adding one fires a 'note_added' notification to assignee + watchers
//   (excluding the author) via the DB trigger on insert
// - Bodies may contain `<@SLACK_USER_ID>` mention markers; the DB trigger
//   converts these into 'note_mention' notifications for each mentioned
//   active team_member (excluding the author).
//
// The composer offers an @-autocomplete dropdown so users don't have to type
// raw Slack ids. NoteRow renders mentions as styled chips inline.
export function NotesPanel({ ticketId }: { ticketId: string }) {
  const { teamMember } = useAuth();
  // Reading notes needs ticket access; adding one needs notes.write in this
  // ticket's inbox (migration 078). RLS refuses the insert regardless.
  const access = useAccess();
  const inboxId = useTicket(ticketId).data?.inbox_id ?? null;
  const canWriteNotes = access.canIn(inboxId, 'notes.write');
  const notesQ = useTicketNotes(ticketId);
  const teamQ = useTeamMembers();
  const addNote = useAddTicketNote();
  const deleteNote = useDeleteTicketNote();
  const [draft, setDraft] = useState('');
  // Imperative handle to the composer's textarea so the emoji picker
  // (rendered in the action row, OUTSIDE NoteComposer) can insert at
  // the caret without us having to hoist the textarea ref / re-do
  // selection math up here.
  const composerHandleRef = useRef<NoteComposerHandle | null>(null);
  // Attachments are uploaded eagerly as the user picks them — the
  // Save click only inserts the row referencing already-uploaded URLs.
  // Cheaper UX-wise: if uploading is slow, the agent sees it
  // happening, not buried inside a single click. Mirrors the assist
  // dialog's pending-tray pattern.
  const [pendingAttachments, setPendingAttachments] = useState<NoteAttachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const teamBySlackId = useMemo(() => {
    const map = new Map<string, TeamMember>();
    for (const m of teamQ.data ?? []) map.set(m.slack_user_id, m);
    return map;
  }, [teamQ.data]);

  // Candidate list for the @-autocomplete: every active team_member except
  // the current user. Stable order by name for predictable arrow-key nav.
  const mentionCandidates = useMemo(() => {
    const all = teamQ.data ?? [];
    return all
      .filter((m) => m.active && m.slack_user_id !== teamMember?.slack_user_id)
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [teamQ.data, teamMember?.slack_user_id]);

  // Transform display-form `@Liz Foo` mentions back into the canonical
  // Slack-format `<@SLACK_ID>` tokens that the DB note-mention trigger
  // expects. We sort candidates by name length descending so longer names
  // win against shorter prefixes (e.g. "@Liz Foo" before "@Liz"). Names
  // that don't exactly match an active team member are left as plain text
  // — that won't trigger a notification, which is the right outcome for
  // typo / non-teammate `@something`.
  const resolveMentions = (text: string): string => {
    const candidates = (teamQ.data ?? [])
      .filter((m) => m.active)
      .slice()
      .sort((a, b) => b.name.length - a.name.length);
    let result = text;
    for (const member of candidates) {
      // Word-boundary-safe match: `@Liz Foo` must not match the middle of
      // `@LizFoot` (no preceding word char before @, no trailing word char).
      // Escape regex meta-chars in the name for safety.
      const escaped = member.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const re = new RegExp(`(^|[^A-Za-z0-9_])@${escaped}(?![A-Za-z0-9_])`, 'g');
      result = result.replace(re, (_full, pre) => `${pre}<@${member.slack_user_id}>`);
    }
    return result;
  };

  const submit = () => {
    const trimmed = draft.trim();
    // A pure-attachment note (no body, just files) is legitimate —
    // dropping a screenshot in chat counts as a note. Only error when
    // BOTH the body AND the attachment tray are empty.
    if (!trimmed && pendingAttachments.length === 0) {
      return toast.error('Type a note or attach a file before saving.');
    }
    if (!teamMember?.slack_user_id) return toast.error('Could not resolve your team member row.');
    const body = resolveMentions(trimmed);
    addNote.mutate(
      {
        ticketId,
        body,
        authorSlackId: teamMember.slack_user_id,
        attachments: pendingAttachments,
      },
      {
        onSuccess: () => {
          setDraft('');
          setPendingAttachments([]);
          toast.success('Note saved');
        },
        onError: (err: unknown) => {
          toastError('Save failed', err);
        },
      },
    );
  };

  async function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const fileList = e.target.files;
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList);
    e.target.value = ''; // reset so re-selecting the same file re-fires
    const room = NOTE_ATTACHMENT_MAX_PER_NOTE - pendingAttachments.length;
    if (room <= 0) {
      toast.error(`Max ${NOTE_ATTACHMENT_MAX_PER_NOTE} files per note.`);
      return;
    }
    const toUpload = files.slice(0, room);
    if (files.length > toUpload.length) {
      toast.info(`Only the first ${room} file(s) will be attached.`);
    }
    setUploading(true);
    try {
      const { attachments, failures } = await uploadNoteAttachments({
        client: supabase,
        ticketId,
        files: toUpload,
      });
      if (attachments.length > 0) setPendingAttachments((p) => [...p, ...attachments]);
      for (const f of failures) toast.error(`${f.filename}: ${f.reason}`);
    } finally {
      setUploading(false);
    }
  }

  function removePendingAttachment(idx: number) {
    setPendingAttachments((p) => p.filter((_, i) => i !== idx));
  }

  const allNotes = notesQ.data ?? [];
  const meSlackId = teamMember?.slack_user_id;
  // Split by `kind` (migration 057). Live assist transcripts get their
  // own tab so they don't drown the team conversation — a busy ticket
  // with multiple assist sessions used to bury every actual note
  // under a wall of transcripts.
  const teamNotes = useMemo(
    () => allNotes.filter((n) => (n.kind ?? 'team_note') === 'team_note'),
    [allNotes],
  );
  const transcripts = useMemo(
    () => allNotes.filter((n) => n.kind === 'assist_transcript'),
    [allNotes],
  );
  // Default to whichever tab has content — but team notes win the tie
  // because that's where new team commentary goes (and the composer
  // lives only there).
  const [activeTab, setActiveTab] = useState<'notes' | 'transcripts'>('notes');

  return (
    // The tab strip and composer go in `pre` so they're never clipped by
    // the height cap — only the list of existing notes below them is.
    <RailSection
      title="Internal notes"
      icon={StickyNote}
      collapsedMaxHeight={18}
      pre={
        <>
      {/* Tab strip — GitHub-PR style underline. Counts in pills so an
          agent landing on the page knows at a glance whether there's
          transcript content waiting. The composer below renders only
          for the Notes tab because transcripts are auto-generated by
          workflow 17; you can't write a new transcript by hand. */}
      <div className="mb-3 flex items-center gap-4 border-b border-border/60">
        <TabPill
          label="Notes"
          count={teamNotes.length}
          active={activeTab === 'notes'}
          onClick={() => setActiveTab('notes')}
        />
        <TabPill
          label="Live assist transcripts"
          count={transcripts.length}
          active={activeTab === 'transcripts'}
          onClick={() => setActiveTab('transcripts')}
          icon={<Sparkles className="h-3 w-3" />}
        />
      </div>

      {activeTab === 'notes' && !canWriteNotes && !access.isLoading ? (
        <p className="mb-3 text-2xs text-muted-foreground">
          Your role can read notes on this ticket but not add them.
        </p>
      ) : null}

      {/* Add-note composer — only on the Notes tab (transcripts are
          auto-generated; you can't compose one by hand). */}
      {activeTab === 'notes' && canWriteNotes && (
      <div className="mb-3 grid gap-1.5">
        <NoteComposer
          ref={composerHandleRef}
          value={draft}
          onChange={setDraft}
          onSubmit={submit}
          candidates={mentionCandidates}
          teamBySlackId={teamBySlackId}
        />
        {/* Pending attachments tray — files already uploaded to the
            `note-attachments` bucket (migration 050), waiting for the
            agent to click Save. Removing here drops the reference; the
            uploaded file lingers in storage but that's fine, the URL
            is unguessable and the storage cost is negligible. */}
        {pendingAttachments.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {pendingAttachments.map((att, i) => (
              <div
                key={`${att.url}-${i}`}
                className="flex items-center gap-1.5 rounded-md border border-border/60 bg-muted/40 px-2 py-1 text-2xs"
              >
                <Paperclip className="h-3 w-3 text-muted-foreground" />
                <span className="max-w-[160px] truncate" title={att.filename}>{att.filename}</span>
                <span className="text-3xs text-muted-foreground">{formatBytes(att.size)}</span>
                <button
                  type="button"
                  onClick={() => removePendingAttachment(i)}
                  className="text-muted-foreground hover:text-foreground"
                  aria-label="Remove attachment"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || pendingAttachments.length >= NOTE_ATTACHMENT_MAX_PER_NOTE}
              title="Attach images or files to this note"
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-border/60 text-muted-foreground hover:bg-accent disabled:opacity-50"
            >
              {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5" />}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              accept="image/*,application/pdf,text/*,.doc,.docx,.xls,.xlsx,.csv,.zip"
            />
            {/* Emoji picker — uses the imperative handle exposed by
                NoteComposer so the picked glyph lands at the caret
                inside the textarea (which lives inside NoteComposer
                and isn't accessible from out here without the ref).
                Defaults: opens upward + right-aligned, fitting the
                composer's position above the note list. */}
            <EmojiPickerButton
              size="sm"
              onPick={(emoji) => composerHandleRef.current?.insertText(emoji)}
            />
            <span className="text-3xs text-muted-foreground">
              Cmd/Ctrl+Enter · type @ to mention
            </span>
          </div>
          <Button
            size="sm"
            onClick={submit}
            disabled={addNote.isPending || uploading}
            className="h-7 px-2.5 text-xs"
          >
            {addNote.isPending ? 'Saving…' : 'Add note'}
          </Button>
        </div>
      </div>
      )}
        </>
      }
    >
      {/* List of existing notes for the active tab. Each tab has its
          own empty-state copy so the agent knows whether they're
          looking at "nothing yet" or "no assist sessions ever ran." */}
      {notesQ.isLoading ? (
        <NotesPanelSkeleton count={2} />
      ) : activeTab === 'notes' ? (
        teamNotes.length === 0 ? (
          <EmptyState
            icon={<StickyNote className="h-4 w-4" />}
            title="No notes yet"
            subtitle="Anything written here is visible only to the team — not the client."
          />
        ) : (
          <ul className="grid gap-2">
            {teamNotes.map((n) => (
              <NoteRow
                key={n.id}
                note={n}
                authorName={assigneeName(n.author_slack_id, teamBySlackId) || n.author_slack_id}
                teamBySlackId={teamBySlackId}
                isMine={meSlackId === n.author_slack_id}
                onDelete={() =>
                  deleteNote.mutate({ id: n.id, ticketId }, {
                    onError: (err: unknown) => {
                      toastError('Delete failed', err);
                    },
                  })
                }
              />
            ))}
          </ul>
        )
      ) : (
        // Transcripts tab — same NoteRow component (transcripts are
        // technically still ticket_notes rows), but no team-composed
        // tab → no compose UI, no @-mentions, no edit. Deletion stays
        // available so an admin can prune a noisy auto-generated row.
        transcripts.length === 0 ? (
          <EmptyState
            icon={<Sparkles className="h-4 w-4" />}
            title="No assist sessions yet"
            subtitle="When a Customer Assist session ends, the chat transcript + shared files land here."
          />
        ) : (
          <ul className="grid gap-2">
            {transcripts.map((n) => (
              <NoteRow
                key={n.id}
                note={n}
                authorName={assigneeName(n.author_slack_id, teamBySlackId) || n.author_slack_id}
                teamBySlackId={teamBySlackId}
                isMine={meSlackId === n.author_slack_id}
                onDelete={() =>
                  deleteNote.mutate({ id: n.id, ticketId }, {
                    onError: (err: unknown) => {
                      toastError('Delete failed', err);
                    },
                  })
                }
              />
            ))}
          </ul>
        )
      )}
    </RailSection>
  );
}

// ───── @-mention composer ────────────────────────────────────────────────

interface NoteComposerProps {
  value: string;
  onChange: (next: string) => void;
  onSubmit: () => void;
  candidates: TeamMember[];
  teamBySlackId: Map<string, TeamMember>;
}

/** Imperative handle the outer NotesPanel uses to inject content at
 *  the textarea caret — currently just emoji from the picker, but the
 *  same hook would suit any future "drop a token in" affordance
 *  (slash-command results, KB snippet insertion, etc.). Centralising
 *  the caret math here (instead of duplicating selectionStart logic
 *  in the parent) keeps the textarea ref local to the component that
 *  owns it. */
export interface NoteComposerHandle {
  insertText: (text: string) => void;
}

// Tab button for the Notes / Live assist transcripts switcher.
// Matches the underline-on-active style used by the Team page tabs
// + the Templates page filter strip so the visual vocabulary stays
// consistent across the app.
function TabPill({
  label,
  count,
  active,
  onClick,
  icon,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
  icon?: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        '-mb-px flex items-center gap-1.5 border-b-2 px-0.5 pb-2 pt-1 text-xs font-medium transition-colors',
        active
          ? 'border-foreground text-foreground'
          : 'border-transparent text-muted-foreground hover:text-foreground',
      )}
    >
      {icon}
      <span>{label}</span>
      {count > 0 ? (
        <span
          className={cn(
            'inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-3xs font-semibold tabular-nums',
            active ? 'bg-foreground text-background' : 'bg-muted text-muted-foreground',
          )}
        >
          {count}
        </span>
      ) : null}
    </button>
  );
}

// Detect an active mention trigger in the textarea. Returns null if the caret
// isn't currently inside an @-mention token, otherwise the start index of the
// `@` and the partial query the user has typed since.
//
// Only fires when `@` is at the start of the string OR preceded by whitespace
// — keeps email-like substrings (foo@bar.com) from triggering the dropdown.
function findActiveMention(
  text: string,
  caret: number,
): { atIndex: number; query: string } | null {
  // Walk back from the caret looking for `@`. Bail on whitespace (no active
  // mention) or other word chars (still inside the query).
  for (let i = caret - 1; i >= 0; i--) {
    const ch = text[i];
    if (ch === '@') {
      const prev = i === 0 ? null : text[i - 1];
      if (prev === null || /\s/.test(prev)) {
        return { atIndex: i, query: text.slice(i + 1, caret) };
      }
      return null;
    }
    if (/\s/.test(ch)) return null;
  }
  return null;
}

// Build highlight segments for the composer backdrop. Splits the typed
// text into plain runs + `@Name` runs that EXACTLY match an active
// team member's display name (the same names the @-autocomplete inserts
// and `resolveMentions` later converts to <@SLACK_ID>). A single
// alternation regex, names sorted longest-first so "@Liz Foo" wins over
// "@Liz". Crucially the highlight is background-colour only (no padding/
// border) so it doesn't shift glyph positions — keeping the backdrop
// char-for-char aligned with the transparent textarea on top.
type ComposerSegment = { text: string; isMention: boolean };
function buildMentionSegments(text: string, names: string[]): ComposerSegment[] {
  if (text.length === 0 || names.length === 0) return [{ text, isMention: false }];
  const escaped = names
    .slice()
    .sort((a, b) => b.length - a.length)
    .map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const re = new RegExp(`(^|[^A-Za-z0-9_])@(${escaped.join('|')})(?![A-Za-z0-9_])`, 'g');
  const out: ComposerSegment[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const pre = m[1] ?? '';
    const mentionStartIdx = m.index + pre.length; // where "@Name" begins
    if (mentionStartIdx > last) {
      out.push({ text: text.slice(last, mentionStartIdx), isMention: false });
    }
    const mentionEnd = m.index + m[0].length;
    out.push({ text: text.slice(mentionStartIdx, mentionEnd), isMention: true });
    last = mentionEnd;
  }
  if (last < text.length) out.push({ text: text.slice(last), isMention: false });
  return out;
}

const NoteComposer = forwardRef<NoteComposerHandle, NoteComposerProps>(function NoteComposer({
  value,
  onChange,
  onSubmit,
  candidates,
  teamBySlackId,
}, ref) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Expose a single insertText() method to the outer panel — used by
  // the emoji picker in the action row. Reads selectionStart / End
  // directly off the DOM so the inserted glyph lands at the caret
  // instead of the end of the string (the naive setDraft(d+e) hack).
  // After update, refocuses + repositions the caret after the inserted
  // text on the next frame (React must apply the value update first).
  useImperativeHandle(ref, () => ({
    insertText: (text: string) => {
      const el = textareaRef.current;
      if (!el) {
        onChange(value + text);
        return;
      }
      const start = el.selectionStart ?? value.length;
      const end = el.selectionEnd ?? start;
      const next = value.slice(0, start) + text + value.slice(end);
      onChange(next);
      requestAnimationFrame(() => {
        el.focus();
        const caret = start + text.length;
        el.setSelectionRange(caret, caret);
      });
    },
  }), [value, onChange]);
  const containerRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [mentionStart, setMentionStart] = useState<number | null>(null);
  const [query, setQuery] = useState('');

  // Highlight ALL active members (not just the autocomplete candidates,
  // which exclude self) so mentioning yourself also highlights. Matches
  // the broader set that `resolveMentions` keys off.
  const highlightNames = useMemo(() => {
    const seen = new Set<string>();
    const names: string[] = [];
    for (const m of teamBySlackId.values()) {
      if (m.name && !seen.has(m.name)) {
        seen.add(m.name);
        names.push(m.name);
      }
    }
    return names;
  }, [teamBySlackId]);

  const segments = useMemo(
    () => buildMentionSegments(value, highlightNames),
    [value, highlightNames],
  );

  // Filter candidates to those whose name starts with the query (case-insensitive).
  const matches = useMemo(() => {
    if (!open) return [] as TeamMember[];
    const q = query.trim().toLowerCase();
    if (q.length === 0) return candidates;
    return candidates.filter((m) => m.name.toLowerCase().startsWith(q));
  }, [open, query, candidates]);

  // Close if the matching set goes empty (per spec).
  useEffect(() => {
    if (open && matches.length === 0) {
      setOpen(false);
      setMentionStart(null);
    }
  }, [open, matches.length]);

  // Clamp activeIdx whenever the match list shrinks.
  useEffect(() => {
    if (activeIdx >= matches.length && matches.length > 0) {
      setActiveIdx(0);
    }
  }, [matches.length, activeIdx]);

  // Click-outside closes the dropdown.
  useEffect(() => {
    if (!open) return;
    function onDocClick(ev: MouseEvent) {
      const root = containerRef.current;
      if (!root) return;
      if (ev.target instanceof Node && !root.contains(ev.target)) {
        setOpen(false);
        setMentionStart(null);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  // Re-evaluate the active mention whenever the textarea contents or caret
  // changes. Called from onChange and onKeyUp/onClick (caret-only moves).
  const reevaluate = (text: string, caret: number) => {
    const found = findActiveMention(text, caret);
    if (!found) {
      if (open) {
        setOpen(false);
        setMentionStart(null);
      }
      return;
    }
    setMentionStart(found.atIndex);
    setQuery(found.query);
    setOpen(true);
    setActiveIdx(0);
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const next = e.target.value;
    onChange(next);
    const caret = e.target.selectionStart ?? next.length;
    reevaluate(next, caret);
  };

  const insertMention = (member: TeamMember) => {
    const ta = textareaRef.current;
    if (!ta || mentionStart === null) return;
    const caret = ta.selectionStart ?? value.length;
    // Insert the human-readable form `@Liz Foo ` while the user is typing.
    // On submit, the parent transforms each `@<exact name>` substring back
    // to the canonical `<@SLACK_ID>` token (which is what the DB
    // mention-trigger expects). This way the textarea never shows raw
    // Slack IDs to the user — they see what they typed.
    const before = value.slice(0, mentionStart);
    const after = value.slice(caret);
    const insertion = `@${member.name} `;
    const next = before + insertion + after;
    onChange(next);
    setOpen(false);
    setMentionStart(null);
    const newCaret = (before + insertion).length;
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(newCaret, newCaret);
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Cmd/Ctrl+Enter still submits even with the dropdown open.
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      onSubmit();
      return;
    }

    if (open && matches.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIdx((idx) => (idx + 1) % matches.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIdx((idx) => (idx - 1 + matches.length) % matches.length);
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        const pick = matches[activeIdx];
        if (pick) insertMention(pick);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
        setMentionStart(null);
        return;
      }
      if (e.key === ' ') {
        // Space ends the mention without picking anything.
        setOpen(false);
        setMentionStart(null);
        // Don't preventDefault — let the space character through.
        return;
      }
    }
  };

  const handleCaretMove = () => {
    const ta = textareaRef.current;
    if (!ta) return;
    reevaluate(ta.value, ta.selectionStart ?? ta.value.length);
  };

  // Keep the backdrop scrolled in lock-step with the textarea so long
  // notes stay aligned when the box overflows.
  const syncScroll = () => {
    const ta = textareaRef.current;
    const bd = backdropRef.current;
    if (ta && bd) {
      bd.scrollTop = ta.scrollTop;
      bd.scrollLeft = ta.scrollLeft;
    }
  };

  // Shared box/typography classes — applied to BOTH the backdrop and the
  // textarea so every glyph lands in the same place. Any change here must
  // be mirrored on both elements or the highlight will drift.
  //
  // `scrollbar-gutter:stable` is what keeps the caret aligned with the
  // backdrop text once a note grows past ~3 lines. Without it the
  // textarea gets a real vertical scrollbar (~15px on Chrome desktop)
  // that eats into its content width, while the `overflow-hidden`
  // backdrop keeps its full width — line-wrap diverges between the
  // two layers, the user sees a growing gap between each word and
  // the caret. Reserving the gutter on BOTH means the content box
  // is byte-for-byte identical regardless of scroll state. Same idea
  // Slack / Linear use for their @-mention composers.
  const sharedBox =
    'min-h-[80px] w-full rounded-md border px-3 py-2 text-xs leading-normal whitespace-pre-wrap break-words [scrollbar-gutter:stable]';

  return (
    <div ref={containerRef} className="relative">
      {/* Highlight backdrop — what the user actually reads. Sits behind a
          transparent-text textarea (which still owns the caret + input).
          aria-hidden + pointer-events-none so it's invisible to AT and
          never steals interaction. Border is transparent so only the
          textarea's border shows, but it still occupies 1px to keep the
          content box identical. */}
      <div
        ref={backdropRef}
        aria-hidden
        className={cn(
          sharedBox,
          // overflow-y-auto (not hidden) so `scrollbar-gutter: stable`
          // from sharedBox actually reserves the gutter. We make the
          // backdrop's scrollbar TRANSPARENT (not `display:none`) so
          // it still occupies its width — that's what keeps the
          // content area the same width as the textarea's content
          // area. `display:none` on the scrollbar would zero out the
          // gutter and re-introduce the original drift bug.
          //
          // Firefox: `scrollbar-color: transparent transparent` paints
          // both track and thumb transparent while keeping the
          // scrollbar's default width.
          // WebKit: `::-webkit-scrollbar` and `…-thumb` get
          // `background: transparent` for the same effect.
          // The textarea on top keeps its real, visible scrollbar.
          'pointer-events-none absolute inset-0 z-0 overflow-y-auto overflow-x-hidden border-transparent text-foreground',
          '[scrollbar-color:transparent_transparent] [&::-webkit-scrollbar]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-transparent [&::-webkit-scrollbar-track]:bg-transparent',
        )}
      >
        {segments.map((seg, i) =>
          seg.isMention ? (
            <span key={i} className="rounded bg-tint-warning-ring/70 text-tint-warning-fg">
              {seg.text}
            </span>
          ) : (
            <span key={i}>{seg.text}</span>
          ),
        )}
        {/* Trailing space so a caret on a final newline keeps the box
            height in sync with the textarea. */}
        {'​'}
      </div>

      <Textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onKeyUp={handleCaretMove}
        onClick={handleCaretMove}
        onScroll={syncScroll}
        placeholder="Add a note for the team. Won't be sent to the client. Type @ to mention."
        rows={2}
        className={cn(
          sharedBox,
          // Transparent text so the highlighted backdrop shows through,
          // but a visible caret + visible placeholder. Relative + z-10 so
          // it paints above the absolutely-positioned backdrop.
          'relative z-10 resize-none bg-transparent text-transparent caret-foreground shadow-none',
        )}
        // Keyboard shortcut target — `n` on a ticket page focuses this textarea.
        data-shortcut-target="ticket-note"
      />
      {open && matches.length > 0 ? (
        <div
          className="absolute left-0 right-0 top-full z-30 mt-1 max-h-[212px] overflow-y-auto rounded-md border border-border/80 bg-popover p-1 shadow-md"
          role="listbox"
        >
          {matches.map((m, idx) => {
            const isActive = idx === activeIdx;
            return (
              <button
                key={m.slack_user_id}
                type="button"
                role="option"
                aria-selected={isActive}
                onMouseEnter={() => setActiveIdx(idx)}
                // mousedown not click — click would fire after the textarea
                // blurs and the dropdown unmounts. mousedown also lets us
                // preventDefault to keep focus in the textarea.
                onMouseDown={(ev) => {
                  ev.preventDefault();
                  insertMention(m);
                }}
                className={cn(
                  'flex w-full cursor-pointer items-center gap-2 rounded px-2 py-1 text-left text-xs',
                  isActive ? 'bg-accent' : 'hover:bg-accent/60',
                )}
              >
                <UserAvatar
                  slackId={m.slack_user_id}
                  teamMembersBySlackId={teamBySlackId}
                  size="sm"
                />
                <span className="truncate font-medium text-foreground/90">{m.name}</span>
                <span className="ml-auto truncate text-3xs text-muted-foreground">
                  {m.email}
                </span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
});

// ───── Note rendering with mention chips + clickable links ─────────────

type NoteSegment =
  | { type: 'text'; value: string }
  | { type: 'mention'; slackId: string }
  | { type: 'link'; href: string };

// Slack-style mention reference produced by the @mention picker:
// `<@U04CEHNVALA>`.
const MENTION_RE = /<@([UWB][A-Z0-9]+)>/g;

// http(s) URLs. Stops at whitespace + a handful of obvious sentence-end
// punctuation we don't want to swallow into the link. Doesn't try to
// match bare domains (foo.com) — too many false positives when an
// agent writes things like "file.ext" or "v1.2".
const URL_RE = /\bhttps?:\/\/[^\s<>"]+?(?=[)\]}.,;:!?'"]*(?:\s|$))/g;

/** Split a note body into text + mention + link segments. Two regex
 *  passes: first collect every match from both regexes with their
 *  ranges, sort by start index, then walk the body emitting plain
 *  text between matches. Keeps the rendering loop simple and lets us
 *  add more segment kinds later without rewriting the parser. */
function parseNoteBody(body: string): NoteSegment[] {
  interface Match {
    start: number;
    end: number;
    segment: Exclude<NoteSegment, { type: 'text' }>;
  }
  const matches: Match[] = [];

  MENTION_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = MENTION_RE.exec(body)) !== null) {
    matches.push({
      start: m.index,
      end: m.index + m[0].length,
      segment: { type: 'mention', slackId: m[1] },
    });
  }

  URL_RE.lastIndex = 0;
  while ((m = URL_RE.exec(body)) !== null) {
    matches.push({
      start: m.index,
      end: m.index + m[0].length,
      segment: { type: 'link', href: m[0] },
    });
  }

  matches.sort((a, b) => a.start - b.start);

  const out: NoteSegment[] = [];
  let cursor = 0;
  for (const match of matches) {
    // If two regexes overlap (e.g. a mention inside a URL — unlikely
    // but possible), drop the second one.
    if (match.start < cursor) continue;
    if (match.start > cursor) {
      out.push({ type: 'text', value: body.slice(cursor, match.start) });
    }
    out.push(match.segment);
    cursor = match.end;
  }
  if (cursor < body.length) {
    out.push({ type: 'text', value: body.slice(cursor) });
  }
  return out;
}

interface RowProps {
  note: TicketNote;
  authorName: string;
  teamBySlackId: Map<string, TeamMember>;
  isMine: boolean;
  onDelete: () => void;
}

function NoteRow({ note, authorName, teamBySlackId, isMine, onDelete }: RowProps) {
  const segments = useMemo(() => parseNoteBody(note.body), [note.body]);
  // Attachments column is jsonb (Json type from the codegen). Narrow
  // to the structurally-compatible AttachmentLike shape — duck-typed
  // since the underlying rows are written by our own code via the
  // useAddTicketNote mutation. Empty array for text-only notes (the
  // common case).
  const attachments = useMemo<NoteAttachment[]>(() => {
    const raw = note.attachments;
    if (!Array.isArray(raw)) return [];
    // Codegen types this as `Json[]` (jsonb is too dynamic for the
    // generator). Build a typed array by hand so we don't have to
    // wrestle with the structural mismatch.
    const out: NoteAttachment[] = [];
    for (const item of raw) {
      if (!item || typeof item !== 'object' || Array.isArray(item)) continue;
      const r = item as Record<string, unknown>;
      if (
        typeof r.url === 'string' &&
        typeof r.filename === 'string' &&
        typeof r.contentType === 'string' &&
        typeof r.size === 'number'
      ) {
        out.push({
          url: r.url,
          filename: r.filename,
          contentType: r.contentType,
          size: r.size,
        });
      }
    }
    return out;
  }, [note.attachments]);

  return (
    <li
      className={cn(
        'group rounded-md border border-tint-warning-ring/60 bg-tint-warning/60 px-2.5 py-2',
      )}
    >
      <div className="mb-1 flex items-center justify-between gap-2 text-2xs text-tint-warning-fg/80">
        <div className="flex min-w-0 items-center gap-1.5">
          <UserAvatar
            slackId={note.author_slack_id}
            teamMembersBySlackId={teamBySlackId}
            size="xs"
          />
          <span className="truncate font-medium">{authorName}</span>
          <span className="shrink-0 text-tint-warning-fg/60">· <RelativeTime value={note.created_at} /></span>
        </div>
        {isMine ? (
          <button
            type="button"
            onClick={onDelete}
            className="cursor-pointer text-tint-warning-fg/60 opacity-0 transition-opacity hover:text-tint-critical-fg group-hover:opacity-100"
            title="Delete note"
            aria-label="Delete note"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        ) : null}
      </div>
      {note.body ? (
        <p className="whitespace-pre-wrap text-xs leading-relaxed text-tint-warning-fg/90 [overflow-wrap:anywhere]">
          {segments.map((seg, i) => {
            if (seg.type === 'text') {
              return <span key={i}>{seg.value}</span>;
            }
            if (seg.type === 'link') {
              return (
                <a
                  key={i}
                  href={seg.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  // Long Supabase storage URLs etc need permission to
                  // wrap mid-string — `break-all` here is intentional
                  // for the link itself even though the surrounding `p`
                  // already has `[overflow-wrap:anywhere]`.
                  className="break-all font-medium text-tint-warning-fg underline underline-offset-2 hover:text-tint-warning-fg"
                >
                  {seg.href}
                </a>
              );
            }
            const name = assigneeName(seg.slackId, teamBySlackId) || seg.slackId;
            return (
              <span
                key={i}
                className="mx-0.5 inline-flex items-center rounded bg-tint-warning-ring/80 px-1 py-px text-2xs font-medium text-tint-warning-fg ring-1 ring-inset ring-tint-warning-ring"
              >
                @{name}
              </span>
            );
          })}
        </p>
      ) : null}
      {attachments.length > 0 ? (
        <div className={cn('flex flex-col gap-1.5', note.body && 'mt-1.5')}>
          {attachments.map((att, i) => (
            <AttachmentTile key={`${att.url}-${i}`} att={att} />
          ))}
        </div>
      ) : null}
    </li>
  );
}
